/* eslint-env browser */
import 'babel-polyfill';
import { useIdleTimer } from 'react-idle-timer';
import PropTypes from 'prop-types';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import regenerateSessionToken from '@core/api/users/regenerateSessionToken';

import { Provider as AulaNextThemeProvider } from '@ublend-npm/aulaui-next';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { StyleRoot } from 'radium';
import { Provider } from 'react-redux';
import { Router, Route, IndexRoute, hashHistory } from 'react-router';
import { syncHistoryWithStore } from 'react-router-redux';
import { configure as configureHotkeys } from 'react-hotkeys';
import { QueryClient, QueryClientProvider } from 'react-query';

import { createCookie, getCookie } from '@core/utils/cookie';
import { connectionOn } from '@core/actions/connection';
import fetchAccessTokenAction from '@core/actions/accessToken/accessToken.action';
import { DARK, THEME } from '@core/constants/theme';
import { THEME_SWITCHING } from '@core/constants/actions';
import { ThemeProvider, AULA_DARK, AULA_LIGHT } from '@ublend-npm/aulaui-core';
import AulaDevTools from './components/containers/common/AulaDevTools';
import App from './components/presentational/App.container';
import DashboardRoute from './routes/Dashboard';
import LogoutRoute from './routes/Logout';
import FilesRoute from './routes/Files';
import SearchRoute from './routes/Search';
import HomeRoute from './routes/Home';
import AllSpacesRoute from './routes/AllSpacesRoute';
import InitialRouteRedirect from './routes/InitialRouteRedirect';
import { loginWithSecureAuthCookie } from '../core/actions/user/login';
import {
  analyticsTrackEvent,
  init as analyticsInit,
} from '../core/utils/analytics';
import { getQueryString } from '../core/utils/queryParams';
import * as analyticsTypes from '../core/constants/analytics';
import Raven from '../core/utils/raven';
import Platform from '../core/utils/platform';
import './fonts/font.css';
import redirectToGeneral from './utils/redirectToGeneral';
import logout from './utils/logout';
import addAxiosInterceptors from './utils/addAxiosInterceptors';
import ActiveDialogBox from './components/containers/ActiveDialogBox';
import useSystemTheme from './hooks/useSystemTheme';
import { isMac } from './utils/device';
import push from './utils/routing';

configureHotkeys({
  ignoreEventsCondition: () => false,
});

const TWENTY_FIVE_MINUTES_INTERVAL = 25 * 60 * 1000;
const THIRTY_MINUTES_INTERVAL = 30 * 60 * 1000;
const THIRTY_SECONDS_INTERVAL = 30 * 1000; // 30 seconds
const DEBOUNCE_TIMER = 500;
let debounceId = 0;
class UserAuthError extends Error {
  constructor(message) {
    super(message);
    this.name = 'UserAuthError';
  }
}

const loadApp = async (store, institutionData, regenerateFunc) => {
  if (regenerateFunc) await regenerateFunc();
  const user = await store.dispatch(loginWithSecureAuthCookie());

  if (!user || user.error) {
    throw new UserAuthError(user.error);
  }

  await analyticsInit(store.getState);
  const loginEvent = getQueryString('login');
  if (loginEvent === 'true') {
    analyticsTrackEvent(analyticsTypes.CODE_LOGIN);
  } else {
    analyticsTrackEvent(analyticsTypes.LAUNCH_AULA);
  }

  Raven.configureScope((scope) => {
    if (
      store.getState() &&
      store.getState().user &&
      store.getState().user.user
    ) {
      scope.setUser({ id: store.getState().user.user.id });
    }
  });

  Raven.setTags({
    institution: institutionData.general.institution,
    Platform,
  });
};

const WebApp = (props) => {
  const { store, institutionData } = props;
  const { getState, dispatch } = store;
  const systemTheme = useSystemTheme();
  const { webAppFtThemeToggle202504, webAppFtOmniSearch202506 } = useFlags();
  const [isLoading, setIsLoading] = useState(true);
  const [history, setHistory] = useState();
  const [isSessionTimedout, setIsSessionTimedout] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(localStorage.getItem(THEME));

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });
  const ldClient = useLDClient();
  const allFlags = ldClient?.allFlags();

  const { institution } = institutionData.general;

  const LATEST_ACTIVITY_COOKIE = `${institution}-latest-activity-timestamp`;
  const SESSION_STATUS_COOKIE = `${institution}-session-status`;

  const setSessionStatusCookie = (value) => {
    createCookie(
      SESSION_STATUS_COOKIE,
      JSON.stringify(value),
      null,
      '/',
      'aula.education'
    );
  };

  const handleRegenerateSessionToken = async () => {
    try {
      // regenerate session token api
      if (!isSessionTimedout) {
        await regenerateSessionToken();
      }
    } catch (error) {
      if (error.status === 401) {
        setIsSessionTimedout(true);
        setSessionStatusCookie({ sessionStatus: false, loggedOut: false });
      }
    }
  };

  const handleSessionLogout = async () => {
    await logout({ returnAfterLogin: true });
    setIsSessionTimedout(false);
    setSessionStatusCookie({ sessionStatus: false, loggedOut: true });
  };

  const handleOnIdle = () => {
    const sessionStatus = JSON.parse(
      unescape(getCookie(SESSION_STATUS_COOKIE))
    );
    if (!sessionStatus.sessionStatus) {
      setIsSessionTimedout(true);
      if (sessionStatus.loggedOut) handleSessionLogout();
      return true;
    }

    const isValidSession =
      new Date().getTime() - Number(getCookie(LATEST_ACTIVITY_COOKIE)) <
      THIRTY_MINUTES_INTERVAL;
    if (!isValidSession) {
      setIsSessionTimedout(true);
      setSessionStatusCookie({ sessionStatus: false, loggedOut: false });
      return true;
    }
    setIsSessionTimedout(false);
    return false;
  };

  const setLatestActivityCookie = async () => {
    const value = new Date().getTime();
    createCookie(LATEST_ACTIVITY_COOKIE, value, null, '/', 'aula.education');
  };

  const onLoadSuccess = () => {
    setHistory(syncHistoryWithStore(hashHistory, store));
    setIsLoading(false);
    addAxiosInterceptors();
    if (allFlags && allFlags['ft-session-token-refresh-enabled-202412']) {
      setSessionStatusCookie({ sessionStatus: true, loggedOut: false });
      setLatestActivityCookie();
    }
  };

  useEffect(() => {
    let regenerateFunc;
    const sessionTokenEnabled =
      allFlags && allFlags['ft-session-token-refresh-enabled-202412'];
    if (sessionTokenEnabled) {
      regenerateFunc = handleRegenerateSessionToken;
    }

    loadApp(store, institutionData, regenerateFunc)
      .then(onLoadSuccess)
      .catch((error) => {
        Raven.captureException(error, {
          tags: {
            context: 'loadApp',
          },
        });

        if (error.name === 'UserAuthError') {
          redirectToGeneral({ returnAfterLogin: true });
          return;
        }

        onLoadSuccess();
      });
    if (sessionTokenEnabled) {
      const interval = setInterval(() => {
        // check wether is the app still loading and also the app has not been idle for 30 minutes to regenerate session token
        handleRegenerateSessionToken();
      }, TWENTY_FIVE_MINUTES_INTERVAL);

      return () => clearInterval(interval);
    }
    return () => {};
  }, []);

  useIdleTimer({
    onAction: setLatestActivityCookie,
    debounce: DEBOUNCE_TIMER,
    disabled: allFlags && !allFlags['ft-session-token-refresh-enabled-202412'],
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (allFlags && allFlags['ft-session-token-refresh-enabled-202412']) {
        handleOnIdle();
      } else {
        clearInterval(interval);
      }
    }, THIRTY_SECONDS_INTERVAL);
    return () => {
      clearInterval(interval);
    };
  }, [allFlags]);

  useEffect(() => {
    const HEARTBEAT_INTERVAL = 5000;
    const SLEEP_DRIFT_THRESHOLD = 2 * HEARTBEAT_INTERVAL;

    let lastTick = Date.now();

    const runDriftCheck = async (runAnyhow = false) => {
      const now = Date.now();
      const drift = now - lastTick;
      lastTick = now;

      if (drift > SLEEP_DRIFT_THRESHOLD || runAnyhow) {
        const idleStatus = handleOnIdle();
        if (idleStatus === false) {
          try {
            await handleRegenerateSessionToken();
          } catch (error) {
            setIsSessionTimedout(true);
            setSessionStatusCookie({ sessionStatus: false, loggedOut: false });
          }
        }
      }
    };

    if (allFlags && allFlags['ft-session-token-refresh-enabled-202412']) {
      const interval = setInterval(runDriftCheck, HEARTBEAT_INTERVAL);
      document.addEventListener('visibilitychange', runDriftCheck);
      window.addEventListener('online', () => {
        runDriftCheck(true);
      });

      return () => {
        document.removeEventListener('visibilitychange', runDriftCheck);
        window.removeEventListener('online', () => {
          runDriftCheck(true);
        });
        clearInterval(interval);
      };
    }

    return () => {};
  }, [allFlags]);

  // To listen system default theme (executes when there is no 'theme' in localStorage )
  useLayoutEffect(() => {
    if (!webAppFtThemeToggle202504) return () => null;
    clearTimeout(debounceId);
    debounceId = setTimeout(() => {
      if (currentTheme === null) {
        dispatch({ type: THEME_SWITCHING, theme: systemTheme });
      }
    }, 1000);
  }, [systemTheme]);

  // To handle theme toggling by button
  useEffect(() => {
    // To initialize the theme from local storage if old flag is enabled
    if (allFlags && allFlags?.WebAppFtAppearance202505) {
      dispatch({ type: THEME_SWITCHING, theme: localStorage.getItem(THEME) });
      // To listen realtime updates into the store
      const unsubscribe = store.subscribe(() => {
        setCurrentTheme(getState()?.ui?.theme);
      });
      return () => unsubscribe();
    }
    return () => null;
  }, [allFlags?.WebAppFtAppearance202505]);

  useEffect(() => {
    if (!webAppFtOmniSearch202506) return;

    const isItMac = isMac();

    const handleKeyDown = (event) => {
      if (
        (isItMac ? event.metaKey : event.ctrlKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === 'f'
      ) {
        event.preventDefault();
        try {
          push('/search');
        } catch {}
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [webAppFtOmniSearch202506]);

  if (isLoading) {
    return null;
  }

  const regenerateSessionTokenForDialogBox = async (spaceId) => {
    await regenerateSessionToken();
    await fetchAccessTokenAction(dispatch)({ spaceId });
    setSessionStatusCookie({ sessionStatus: true, loggedOut: false });
    dispatch(connectionOn());
    setIsSessionTimedout(false);
  };

  return (
    <>
      <AulaNextThemeProvider>
        <StyleRoot>
          <Provider store={store}>
            {webAppFtThemeToggle202504 ? (
              <ThemeProvider
                theme={currentTheme === DARK ? AULA_DARK : AULA_LIGHT}
              >
                {allFlags &&
                  allFlags['ft-session-token-refresh-enabled-202412'] && (
                    <ActiveDialogBox
                      open={isSessionTimedout}
                      handleRegenerateSessionToken={
                        regenerateSessionTokenForDialogBox
                      }
                      handleSessionLogout={handleSessionLogout}
                    />
                  )}
                <QueryClientProvider client={queryClient}>
                  <Router history={history}>
                    <Route path="/" component={App}>
                      {LogoutRoute}
                      {DashboardRoute(queryClient, ldClient)}
                      {FilesRoute}
                      {SearchRoute(queryClient, ldClient)}
                      {HomeRoute(queryClient, ldClient)}
                      {AllSpacesRoute(queryClient, ldClient)}
                      <IndexRoute component={InitialRouteRedirect} />
                    </Route>
                  </Router>
                </QueryClientProvider>
              </ThemeProvider>
            ) : (
              <>
                {allFlags &&
                  allFlags['ft-session-token-refresh-enabled-202412'] && (
                    <ActiveDialogBox
                      open={isSessionTimedout}
                      handleRegenerateSessionToken={
                        regenerateSessionTokenForDialogBox
                      }
                      handleSessionLogout={handleSessionLogout}
                    />
                  )}
                <QueryClientProvider client={queryClient}>
                  <Router history={history}>
                    <Route path="/" component={App}>
                      {LogoutRoute}
                      {DashboardRoute(queryClient, ldClient)}
                      {FilesRoute}
                      {SearchRoute(queryClient, ldClient)}
                      {HomeRoute(queryClient, ldClient)}
                      {AllSpacesRoute(queryClient, ldClient)}
                      <IndexRoute component={InitialRouteRedirect} />
                    </Route>
                  </Router>
                </QueryClientProvider>
              </>
            )}
          </Provider>
        </StyleRoot>
        <AulaDevTools />
      </AulaNextThemeProvider>
    </>
  );
};

WebApp.propTypes = {
  store: PropTypes.shape().isRequired,
  institutionData: PropTypes.shape().isRequired,
};

export default WebApp;
